/**
 * What the preview is pointed at, and when.
 *
 * Two sources feed the simulator and they are deliberately different things.
 *
 * A LIVE screen is previewed from the manifest the API builds for it — the same
 * bytes the stick will fetch, opening hours, dayparts, override and all. Nothing
 * is reconstructed in the browser, so the preview cannot flatter the server: if
 * the builder drops a playlist whose asset expired, the preview loses it too.
 *
 * A DRAFT playlist is previewed from a manifest assembled here, because the
 * playlist being edited does not exist on the server yet and by definition has no
 * screen. It carries the one playlist and its assets and nothing else — no
 * opening hours, no schedule — so it always plays, and the panel says so rather
 * than letting somebody read "closed" as a verdict on their playlist.
 *
 * The clock is a third axis. `resolve()` takes the instant as an argument, which
 * is what makes "show me 11am on a Tuesday" an honest question rather than a
 * mock: the answer comes out of the same function the TV runs, fed a different
 * number. Scrubbing is expressed as an OFFSET from the real clock, not a frozen
 * instant, so time keeps running from the moment chosen and a daypart boundary
 * two minutes ahead of it still arrives while you watch.
 */

import { z } from 'zod';
import {
  assetTypeSchema,
  deviceManifestSchema,
  manifestScreenSchema,
  zonedParts,
} from '@pbtv/shared';
import type {
  DeviceManifest,
  ManifestAsset,
  PlaylistItem,
  RotationDegrees,
  ZonedParts,
} from '@pbtv/shared';
import { apiRequest } from './api.js';
import { entityOf } from './envelope.js';
import type { MediaAsset } from './media.js';
import { DEFAULT_PREVIEW_RESOLUTION } from '../components/preview/geometry.js';
import type { PreviewResolution } from '../components/preview/geometry.js';

// ---------------------------------------------------------------------------
// The screen being previewed
// ---------------------------------------------------------------------------

/**
 * The reported framebuffer, read leniently. A screen that has never sent a
 * heartbeat has no `deviceInfo` at all, and a partial one from an older app build
 * must not take the whole preview down with it — hence `.catch(null)` rather than
 * a hard parse. The fallback is the landscape 1080p a Fire TV Stick drives by
 * default, which is what the preview showed before this route existed.
 */
const reportedResolutionSchema = z
  .object({
    screenWidthPx: z.number().int().positive(),
    screenHeightPx: z.number().int().positive(),
  })
  .nullish()
  .catch(null);

const screenDetailSchema = entityOf(
  z.object({
    screen: manifestScreenSchema,
    storeId: z.string().min(1),
    storeName: z.string().default(''),
    deviceInfo: reportedResolutionSchema,
  }),
  'detail',
  'screen',
);

export interface PreviewScreen {
  screenId: string;
  storeId: string;
  storeName: string;
  name: string;
  rotationDegrees: RotationDegrees;
  resolution: PreviewResolution;
  /** False when the resolution is the assumed default, not something a TV said. */
  resolutionReported: boolean;
}

export function previewScreenQueryKey(screenId: string): readonly string[] {
  return ['admin', 'screens', screenId, 'preview'];
}

export function screenManifestQueryKey(screenId: string): readonly string[] {
  return ['admin', 'screens', screenId, 'manifest'];
}

export async function getPreviewScreen(
  screenId: string,
  signal?: AbortSignal,
): Promise<PreviewScreen> {
  const detail = await apiRequest(
    screenDetailSchema,
    `/admin/screens/${encodeURIComponent(screenId)}`,
    { signal },
  );

  const reported = detail.deviceInfo ?? null;

  return {
    screenId: detail.screen.id,
    storeId: detail.storeId,
    storeName: detail.storeName,
    name: detail.screen.name,
    rotationDegrees: detail.screen.rotationDegrees,
    resolution:
      reported === null
        ? DEFAULT_PREVIEW_RESOLUTION
        : { width: reported.screenWidthPx, height: reported.screenHeightPx },
    resolutionReported: reported !== null,
  };
}

const manifestEnvelopeSchema = entityOf(deviceManifestSchema, 'manifest');

/** The screen's real manifest, validated against the contract before it is played. */
export async function fetchScreenManifest(
  screenId: string,
  signal?: AbortSignal,
): Promise<DeviceManifest> {
  return apiRequest(
    manifestEnvelopeSchema,
    `/admin/screens/${encodeURIComponent(screenId)}/manifest`,
    { signal },
  );
}

// ---------------------------------------------------------------------------
// The draft playlist
// ---------------------------------------------------------------------------

/**
 * A draft manifest never leaves the browser, is never hashed and is never handed
 * to a device, so the two fields that exist purely for the device — the version
 * and each asset's digest — are labelled rather than invented. Writing a
 * plausible-looking hex digest here would be a lie with a long life: it would be
 * indistinguishable from a real one in a screenshot or a bug report.
 */
const DRAFT_VERSION = 'draft-preview';
const DRAFT_SHA256 = '0'.repeat(64);

export interface DraftManifestInput {
  screen: { id: string; name: string; rotationDegrees: RotationDegrees };
  /** The owning store's zone. Inert here — a draft has no hours and no dayparts. */
  timezone: string;
  playlistId: string;
  playlistName: string;
  items: readonly PlaylistItem[];
  assets: readonly MediaAsset[];
  nowMs: number;
}

/**
 * Builds a manifest for the playlist being edited. It goes through
 * `deviceManifestSchema` exactly as the API's output does, so the preview runs
 * the resolver over a document that satisfies the contract rather than over a
 * hand-shaped object that happens to have the right field names.
 *
 * Opening hours and the schedule are left empty on purpose: a draft playlist is
 * not assigned to anything yet, so there is no daypart it belongs to and no
 * trading window that could close it. What this shows is the loop itself.
 */
export function draftManifest(input: DraftManifestInput): DeviceManifest {
  const assets: Record<string, ManifestAsset> = {};

  for (const asset of input.assets) {
    const type = assetTypeSchema.safeParse(asset.type);
    // v1 accepts exactly four media types; anything else could not have been
    // uploaded, and guessing at it here would put an unplayable item on screen.
    if (!type.success) continue;
    assets[asset.assetId] = {
      id: asset.assetId,
      type: type.data,
      url: asset.url,
      sha256: DRAFT_SHA256,
      bytes: Math.max(1, asset.bytes),
      width: Math.max(1, asset.width),
      height: Math.max(1, asset.height),
      durationMs: asset.durationMs,
      contentExpiresAt: asset.contentExpiresAt,
    };
  }

  const items = input.items.filter((item) => assets[item.assetId] !== undefined);
  const referencedBytes = Object.values(assets).reduce((total, asset) => total + asset.bytes, 0);

  return deviceManifestSchema.parse({
    manifestVersion: DRAFT_VERSION,
    generatedAt: new Date(input.nowMs).toISOString(),
    screen: input.screen,
    timezone: input.timezone,
    openingHours: [],
    schedule: [],
    defaultPlaylistId: input.playlistId,
    playlists: {
      [input.playlistId]: {
        id: input.playlistId,
        name: input.playlistName,
        items,
      },
    },
    assets,
    polling: {},
    referencedBytes,
  });
}

// ---------------------------------------------------------------------------
// Scrubbing the clock, in the store's zone
// ---------------------------------------------------------------------------

const DAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** The offset, in milliseconds, between this instant and the wall clock in `timeZone`. */
function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = zonedParts(instantMs, timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - instantMs;
}

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/**
 * The instant at which `timeZone` reads this wall clock.
 *
 * Two passes, because the offset to apply depends on the answer: the first guess
 * uses the offset in force at the same numbers read as UTC, the second uses the
 * offset in force at that guess. That converges everywhere except across the two
 * daylight-saving seams, where converging is not possible because the question
 * has no single answer:
 *
 *   - The hour that never happens (NZ, late September) resolves forward, so
 *     02:30 on changeover Sunday reads as 03:30. The preview shows the instant
 *     the TV will actually be at.
 *   - The hour that happens twice (NZ, early April) resolves to the SECOND pass,
 *     the one in standard time. Either is defensible; picking one and pinning it
 *     in a test is what stops it drifting.
 */
export function instantInZone(clock: WallClock, timeZone: string): number {
  const asUtc = Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute, 0);
  const firstGuess = asUtc - zoneOffsetMs(asUtc, timeZone);
  return asUtc - zoneOffsetMs(firstGuess, timeZone);
}

/** The `<input type="date">` and `<input type="time">` values for an instant. */
export function scrubFields(instantMs: number, timeZone: string): { date: string; time: string } {
  const parts = zonedParts(instantMs, timeZone);
  return {
    date: `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`,
    time: `${pad2(parts.hour)}:${pad2(parts.minute)}`,
  };
}

const DATE_INPUT = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_INPUT = /^(\d{2}):(\d{2})$/;

/**
 * Reads those two fields back. Returns null while either is empty or half-typed,
 * which is the normal state of a date field somebody is in the middle of using —
 * the caller holds the previous instant rather than jumping to 1970.
 */
export function parseScrubFields(date: string, time: string, timeZone: string): number | null {
  const dateMatch = DATE_INPUT.exec(date.trim());
  const timeMatch = TIME_INPUT.exec(time.trim());
  if (dateMatch === null || timeMatch === null) return null;

  const clock: WallClock = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
  };

  if (clock.month < 1 || clock.month > 12) return null;
  if (clock.day < 1 || clock.day > 31) return null;
  if (clock.hour > 23 || clock.minute > 59) return null;

  return instantInZone(clock, timeZone);
}

/** "Tuesday 25 August, 11:00" — store-local, which is the only clock that decides anything. */
export function describeInstant(instantMs: number, timeZone: string): string {
  const parts: ZonedParts = zonedParts(instantMs, timeZone);
  const day = DAY_NAMES[parts.isoDayOfWeek - 1] ?? '';
  const month = MONTH_NAMES[parts.month - 1] ?? '';
  return `${day} ${parts.day} ${month}, ${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

/**
 * The offset the preview runs on. Zero means "now"; anything else means the
 * simulator's clock is that far from this browser's, and keeps running.
 */
export function offsetTo(instantMs: number, nowMs: number): number {
  return instantMs - nowMs;
}

/**
 * How far from real time the preview is, said in words, or null when it is close
 * enough to now to be worth nothing. The minute band runs to two hours rather
 * than one so that "90 minutes ahead" is not rounded into "2 hours ahead", which
 * would be wrong by half an hour in the one place somebody is hunting for a
 * daypart boundary.
 */
export function describeOffset(offsetMs: number): string | null {
  const magnitude = Math.abs(offsetMs);
  if (magnitude < 60_000) return null;

  const minutes = Math.round(magnitude / 60_000);
  const direction = offsetMs > 0 ? 'ahead of' : 'behind';

  if (minutes < 120) return `${minutes === 1 ? '1 minute' : `${minutes} minutes`} ${direction} now`;

  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hours ${direction} now`;

  return `${Math.round(hours / 24)} days ${direction} now`;
}
